import { useEffect, useRef } from "react";

const EDGE_PX = 28;
const COMMIT_PX = 90;
const ENGAGE_PX = 12;

/** iPhone-style edge swipe: drag from the left edge to go back. Attach the returned ref to the swipe area; `pageRef` gets the drag feedback. */
export function useSwipeBack({ enabled, onBack, pageRef }) {
  const ref = useRef(null);
  const state = useRef(null);
  const backRef = useRef(onBack);
  backRef.current = onBack;

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return undefined;

    const page = () => pageRef?.current;
    const dialogOpen = () => !!document.querySelector('[role="dialog"][data-state="open"]');

    function reset(animated) {
      const p = page();
      if (p) {
        p.style.transition = animated ? "transform 200ms cubic-bezier(0.32,0.72,0,1), opacity 200ms" : "";
        p.style.transform = "";
        p.style.opacity = "";
        if (animated) setTimeout(() => { p.style.transition = ""; }, 220);
      }
      el.style.overflowX = "";
      state.current = null;
    }

    function onStart(e) {
      if (e.touches.length !== 1 || dialogOpen()) return;
      const t = e.touches[0];
      if (t.clientX > EDGE_PX) return;
      state.current = { x: t.clientX, y: t.clientY, engaged: false, dx: 0 };
    }

    function onMove(e) {
      const s = state.current;
      if (!s) return;
      const t = e.touches[0];
      const dx = t.clientX - s.x, dy = t.clientY - s.y;
      if (!s.engaged) {
        if (Math.abs(dy) > ENGAGE_PX && Math.abs(dy) > dx) { state.current = null; return; }
        if (dx < ENGAGE_PX) return;
        s.engaged = true;
        el.style.overflowX = "clip";
      }
      e.preventDefault();
      const p = page();
      if (p) {
        const shift = Math.max(0, dx);
        p.style.transition = "none";
        p.style.transform = `translateX(${shift}px)`;
        p.style.opacity = String(Math.max(0.55, 1 - shift / (window.innerWidth * 1.4)));
      }
      s.dx = dx;
    }

    function onEnd() {
      const s = state.current;
      if (!s || !s.engaged) { state.current = null; return; }
      if (s.dx < COMMIT_PX) { reset(true); return; }
      const p = page();
      if (p) {
        p.style.transition = "transform 180ms ease-out, opacity 180ms ease-out";
        p.style.transform = "translateX(100%)";
        p.style.opacity = "0.4";
      }
      state.current = null;
      setTimeout(() => { backRef.current(); reset(false); }, 170);
    }

    const onCancel = () => reset(true);
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onCancel);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onCancel);
      reset(false);
    };
  }, [enabled, pageRef]);

  return ref;
}
